/* eslint-disable max-len */
import t from '@rcm/i18n-client';
import './BankTransfer.scss';
import {
  InvisibleReCaptcha2Button,
  useCaptchaActionEnabled,
} from '@rcm/recaptcha-2';
import type { FormEvent } from 'react';
import React, { useCallback, useState } from 'react';
import { BANK_TRANSFER } from '@reliv/cart';
import { Form } from 'forms';
import { getCheckoutOrderManager } from 'cart-ui';
import {
  selectCartInfo,
  selectIsEditing,
} from 'cart-ui-react/src/autoshipState/selectors';
import {
  setEditingAlert,
  setSnackbar,
} from 'cart-ui-react/src/autoshipState/autoshipSlice';
import { useSelector, useDispatch } from 'react-redux';

interface Props {
  order: any;
  onSubmit: (data: { type: string; data: { captcha?: string } }) => void;
  paymentType: string;
  disabled?: boolean;
  isCartV2?: boolean;
  setError?: any;
}

const BankTransfer = ({
  onSubmit,
  paymentType,
  disabled = false,
  isCartV2 = false,
  setError,
}: Props) => {
  const reCaptcha2Enabled = useCaptchaActionEnabled(BANK_TRANSFER);
  const [captcha, setCaptcha] = useState('');
  const dispatch = useDispatch();
  const cartV2Info = useSelector(selectCartInfo);
  const isEditing = useSelector(selectIsEditing);

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      onSubmit({
        type: paymentType,
        data: {
          ...(reCaptcha2Enabled ? { captcha } : {}),
        },
      });
    },
    [captcha, onSubmit, paymentType, reCaptcha2Enabled]
  );
  const handleEditingAlert = () => {
    if (isCartV2 && isEditing) {
      dispatch(setEditingAlert(true));
      dispatch(
        setSnackbar({
          message: t('Please save any changes before you proceed.'),
          type: 'warning',
          delay: 4500,
        })
      );
      setTimeout(() => {
        dispatch(setEditingAlert(false));
      }, 100);
      return;
    }
  };

  const handleSubmitV2 = useCallback(
    (event) => {
      event.preventDefault();
      getCheckoutOrderManager().then(async (orderManager) => {
        // check for cart changes (mainly Kalogris donations but any frontend cart changes would get applied here)
        await orderManager.patchOrder(cartV2Info);
        await orderManager
          .processCurrentOrder({
            type: paymentType,
            data: {
              ...(reCaptcha2Enabled ? { captcha } : {}),
            },
          })
          .then(() => {
            const paymentError = JSON.parse(
              localStorage.getItem('paymentError')
            );
            if (paymentError) {
              dispatch(setError(paymentError[0].value));
            }
          });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [captcha, onSubmit, paymentType, reCaptcha2Enabled]
  );

  return (
    <Form onSubmit={isCartV2 ? handleSubmitV2 : handleSubmit}>
      {!isCartV2 ? (
        <div className='cartHeader'>
          <div className='col-xs-10'>{t('Bank Transfer')}</div>
        </div>
      ) : null}
      <div className='col-sm-12 col-xs-10'>
        <p>
          {t(
            'Your order will be held until the funds have been received by Reliv Europe.'
          )}
        </p>
        <p>{t('Please make sure you transfer to the following account:')}</p>
        <p>{t('Sort code: 20-98-68')}</p>
        <p>{t('Account number: 20578959')}</p>
        <p>
          {t(
            'Please provide your order number on your bank transfer to us, this will ensure your order is sent out as quick as possible once the funds have been received. If you have any questions, please contact the Reliv Europe head office on 01527592878.'
          )}
        </p>
      </div>
      <div onClick={handleEditingAlert}>
        <div
          style={
            disabled || isEditing ? { pointerEvents: 'none', opacity: 0.7 } : {}
          }
        >
          <InvisibleReCaptcha2Button
            action={BANK_TRANSFER}
            btn='primary'
            className='continueButton'
            disabled={disabled}
            onValueChange={setCaptcha}
            text={t('Place Order')}
            type='submit'
          />
        </div>
      </div>
    </Form>
  );
};

export default BankTransfer;
